import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/auth/AuthProvider';
import {
  useServicesNavTree,
  type ServicesNavLeaf,
} from '@/pages/services/use-services-nav-tree';

/**
 * Pinned services — the user's quick-access shortcuts shown in the sidebar and on
 * the Overview dashboard. Persisted client-side per signed-in user (localStorage);
 * @mgmt has no per-user preferences store, and a pin list is a UI convenience, so
 * a DB-backed store isn't warranted. The Provider is the single source of truth so
 * the topbar star, the sidebar section and the dashboard block stay in sync.
 * Swap the load/save pair for a backend feed later if cross-device sync is wanted.
 */

const STORE_VERSION = 'v1';

function storageKey(userId: string | undefined): string {
  return `partnercfg.pinnedServices/${STORE_VERSION}/${userId || 'anon'}`;
}

function load(userId: string | undefined): string[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

interface PinnedContextValue {
  pinnedIds: string[];
  isPinned: (id: string) => boolean;
  toggle: (id: string) => void;
}

const PinnedContext = createContext<PinnedContextValue | null>(null);

export function PinnedServicesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.uuid;
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => load(userId));

  // Reload when the signed-in user changes (login / impersonation switch).
  useEffect(() => {
    setPinnedIds(load(userId));
  }, [userId]);

  // Persist on every change (best-effort — ignore quota / private-mode errors).
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(pinnedIds));
    } catch {
      // ignore
    }
  }, [userId, pinnedIds]);

  const value = useMemo<PinnedContextValue>(
    () => ({
      pinnedIds,
      isPinned: (id) => pinnedIds.includes(id),
      toggle: (id) =>
        setPinnedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])),
    }),
    [pinnedIds],
  );

  return <PinnedContext.Provider value={value}>{children}</PinnedContext.Provider>;
}

export function usePinnedServices(): PinnedContextValue {
  const ctx = useContext(PinnedContext);
  if (!ctx) throw new Error('usePinnedServices must be used within PinnedServicesProvider');
  return ctx;
}

/**
 * The pinned services resolved to live nav leaves, in pin order. Stale ids (a
 * service that was unprovisioned or is no longer visible) are dropped, so callers
 * never render a dead shortcut.
 */
export function usePinnedLeaves(): ServicesNavLeaf[] {
  const { pinnedIds } = usePinnedServices();
  const tree = useServicesNavTree();
  return useMemo(() => {
    const byId = new Map(tree.flatMap((c) => c.services.map((s) => [s.id, s] as const)));
    return pinnedIds
      .map((id) => byId.get(id))
      .filter((s): s is ServicesNavLeaf => !!s);
  }, [pinnedIds, tree]);
}

/**
 * The provisioned-service leaf the current route is inside, or null when the
 * route isn't a service console. Drives the topbar "pin this service" star.
 */
export function useCurrentServiceLeaf(pathname: string): ServicesNavLeaf | null {
  const tree = useServicesNavTree();
  return useMemo(() => {
    for (const c of tree) {
      for (const s of c.services) {
        if (pathname === s.route || pathname.startsWith(`${s.route}/`)) return s;
      }
    }
    return null;
  }, [tree, pathname]);
}
